import { Component, OnInit, Optional, ViewChild } from '@angular/core';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { IPSObservabilityService } from '@ips/common-angular';
import { ClaimItemService } from 'com.trumbull.phx.claim.api-client';
import { DocumentItemList1, DocumentService } from 'com.trumbull.phx.document.api-client';
import { OrderService } from 'com.trumbull.phx.order.api-client';
import { VocabularyService } from 'com.trumbull.phx.vocabulary.api-client';
import { ButtonModule } from 'primeng/button';
import { PanelModule } from 'primeng/panel';
import { TabsModule } from 'primeng/tabs';
import { catchError, combineLatest, filter, finalize, Observable, of, switchMap, tap } from 'rxjs';
import { DocumentProcessingService } from '../../services/document-processor/document-processor.service';
import { OrderDetailsMapperService } from '../../services/order-details-mapper/order-details-mapper.service';
import { ModalComponent } from '../../shared/modal/modal.component';
import { NeedHelpModalComponent } from '../../shared/modal/need-help-modal/need-help-modal.component';
import { VocabApiService } from '../../vocab/vocab.service';
import { OrderDetailsGeneralInformationComponent } from './order-details-general-information/order-details-general-information.component';
import { OrderDetails } from './order-details.model';
import { RootObject } from './orders.model';
import { DocumentReference, RecordIndexTableComponent, TableData } from './record-index-table/record-index-table.component';

@Component({
  selector: 'app-order-details',
  imports: [
    PanelModule,
    TabsModule,
    ButtonModule,
    OrderDetailsGeneralInformationComponent,
    ModalComponent,
    ButtonModule,
    RecordIndexTableComponent,
    NeedHelpModalComponent
  ],
  templateUrl: './order-details.component.html',
  styleUrls: ['./order-details.component.scss']
})
export class OrderDetailsComponent implements OnInit {
  @ViewChild(RecordIndexTableComponent)
  recordIndexTableComponent!: RecordIndexTableComponent;
  orderDetails?: OrderDetails;
  orderData?: RootObject;
  orderDocuments?: (DocumentItemList1 | undefined)[]
  showHelpModal: boolean = false;
  orderId$: Observable<string | null>;
  orderNotFound: boolean = false;

  recordRequests: TableData[] = [];

  isOrderLoading = false;
  isDocumentsLoading = false;

  constructor(
    private readonly orderService: OrderService,
    private readonly vocabService: VocabularyService,
    private readonly claimService: ClaimItemService,
    private readonly documentService: DocumentService,
    private readonly documentProcessor: DocumentProcessingService,
    private readonly orderDetailsMapperService: OrderDetailsMapperService,
    private route: ActivatedRoute,
    private router: Router,
    private vocabApiService: VocabApiService,
    @Optional() private observability: IPSObservabilityService
  ) {
    this.orderId$ = this.route.paramMap.pipe(
      switchMap((params: ParamMap) => {
        return new Observable<string | null>(observer => {
          observer.next(params.get('id'));
          observer.complete();
        });
      })
    );
  }

  ngOnInit(): void {
    this.orderId$.pipe(
      filter(id => !!id),
      switchMap((id: string | null) => {
        this.isOrderLoading = true;
        this.isDocumentsLoading = true;

        if (!id) {
          return of(null);
        }

        const order$ = this.orderService.getOrder('standard', id).pipe(
          catchError(error => {
            console.error('Error fetching order:', error);
            return of(null);
          })
        );

        const orderDetails$ = this.orderService.getOrderData('standard', id).pipe(
          tap(details => {
            if (details) {
              this.orderData = details as RootObject;
            }
          }),
          catchError(error => {
            console.error('Error fetching details:', error);
            return of(null);
          })
        );

        const vocabData$ = this.vocabApiService.getVocabData(['OrderType', 'ContactTitle', 'PhysicalLocationUsAddressStateCode', 'RecordTypes']).pipe(
          catchError(error => {
            console.error('Error fetching order:', error);
            return of(null);
          }));

        // Combine order and details first to ensure proper mapping
        const mappedData$ = combineLatest([order$, orderDetails$, vocabData$]).pipe(
          tap(([order, details, vocabData]) => {
            if (order && details) {
              this.orderDetails = this.orderDetailsMapperService.mapOrderDetails(order, details as RootObject, vocabData);
              this.recordRequests = this.documentProcessor.mapRequests(order, details as RootObject, undefined,vocabData);
              this.isOrderLoading = false;
            }
          })
        );

        const claim$ = this.claimService.listClaims(
          'standard', undefined, undefined, undefined, undefined, undefined, id
        ).pipe(
          catchError(error => {
            console.error('Error fetching claim:', error);
            return of(null);
          })
        );

        return combineLatest([mappedData$, claim$, vocabData$]).pipe(
          tap(([mappedData, claim, vocabData]) => {
            let order = mappedData[0];
            let details = mappedData[1];
            // FIXME: Possible to not remap these and only map docs

            if (claim?.claimItems?.[0] && order && details) {
              this.documentProcessor.fetchDocuments(claim?.claimItems?.[0], loading => this.isDocumentsLoading = loading)
                .subscribe(documents => {
                  this.orderDocuments = documents;
                  const documentListPattern = /^[^-]+ - (\d+) - .+$/;
                  const processedDocs = this.documentProcessor.filterDocumentReferences(this.orderDocuments || [], documentListPattern);
                  this.recordRequests = this.documentProcessor.mapRequests(order, details as RootObject, documents, vocabData);

                  this.observability?.trackEvent('document:processing:filter:results', processedDocs.stats);
                });
            }
          }),
          catchError(error => {
            console.error('Error in data processing:', error);
            if (error.status >= 401) {
              this.orderNotFound = true;
              this.router.navigate(['/home']);
            }
            return of([null, null]);
          }),
          finalize(() => this.isDocumentsLoading = false)
        );
      })
    ).subscribe();
  }

  needHelp() {
    this.showHelpModal = true;
  }

  tableAction(data: TableEvent | any) {
    let type = data?.action || data;
    switch (type) {
      case 'expandAll':
        this.recordIndexTableComponent.expandAll();
        break;
      case 'collapseAll':
        this.recordIndexTableComponent.collapseAll();
        break;
      case 'downloadAll':
        break;
      case 'pageChange':
        break;
      case 'downloadSingleFile':
        if (!data || typeof data !== 'object' || !('action' in data)) {
          return;
        }

        this.documentService.getDocumentContent(
          'standard',
          (data.data as DocumentReference)?.documentListId || '',
          (data.data as DocumentReference)?.documentId || ''
        ).subscribe(response => {
          if (response) {
            this.downloadFile(response, data);
          }
        });

        break;
      case 'downloadMultipleFiles':

        this.documentService.getDocumentContentZip('standard', data?.data.documentListId || '')
          .subscribe(response => {
            this.downloadFile(response, data, true);
          });

        break;
      case 'downloadAllFiles':
        let documentListIds = new Set();

        data.data.forEach((doc: any) => {
          doc.documents.forEach((d: any) => {
            documentListIds.add(d.documentListId);
          })
        });

        documentListIds.forEach((documentListId: any) => {
          this.documentService.getDocumentContentZip('standard', documentListId || '')
            .subscribe(response => {
              this.downloadFile(response, data, true);
            });
        });

        break;
      default:
        throw new Error(`Unknown action: ${type}`);
    }
  }

  private downloadFile(response: Blob, data: any, archive = false) {
    const blob = new Blob([response as any], { type: 'application/octet-stream' });
    const url = window.URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = archive ? `download.zip` : (data.data as DocumentReference)?.fileName || 'document';
    document.body.appendChild(a);

    a.click();

    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  }

  downloadAll() {
    this.tableAction({ action: 'downloadAllFiles', data: this.recordRequests });
  }

  hasDocs() {
    return this.recordRequests?.some(request => request.documents && request.documents.length > 0);
  }
}

interface TableEvent {
  action: string
  data: DocumentReference | DocumentReference[] | any
}
